import PropTypes from 'prop-types';
import { usePermission } from '@hooks';

/**
 * Componente wrapper para vistas/rutas completas que requieren permisos
 * Muestra un mensaje de acceso denegado si el usuario no tiene permisos
 *
 * @example
 * <ProtectedRoute permission="admin.gestionar_roles">
 *   <RolesPage />
 * </ProtectedRoute>
 */
export function ProtectedRoute({
  permission,
  permissions = [],
  requireAll = false,
  children,
  showAccessDenied = true,
}) {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermission();

  const allPermissions = permission ? [permission, ...permissions] : permissions;

  // Si no se especifican permisos, mostrar children
  if (allPermissions.length === 0) {
    return children;
  }

  const hasAccess = requireAll
    ? hasAllPermissions(allPermissions)
    : hasAnyPermission(allPermissions);

  if (!hasAccess) {
    if (!showAccessDenied) return null;

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          padding: '32px',
        }}
      >
        <div
          style={{
            textAlign: 'center',
            maxWidth: '400px',
            padding: '32px',
            borderRadius: '12px',
            backgroundColor: '#f8fafc',
            border: '1px solid #e2e8f0',
          }}
        >
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
          <h2
            style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#1e293b',
              marginBottom: '8px',
            }}
          >
            Acceso Denegado
          </h2>
          <p
            style={{
              fontSize: '14px',
              color: '#64748b',
              margin: 0,
            }}
          >
            No tienes permisos para acceder a esta sección. Contacta a un administrador si crees que
            esto es un error.
          </p>
        </div>
      </div>
    );
  }

  return children;
}

ProtectedRoute.propTypes = {
  /** Permiso único requerido */
  permission: PropTypes.string,
  /** Lista de permisos (combinados con permission si se proporciona) */
  permissions: PropTypes.arrayOf(PropTypes.string),
  /** Si es true, requiere TODOS los permisos */
  requireAll: PropTypes.bool,
  /** Contenido protegido */
  children: PropTypes.node.isRequired,
  /** Mostrar mensaje de acceso denegado (default: true) */
  showAccessDenied: PropTypes.bool,
};

export default ProtectedRoute;
